import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  adminService,
  type AdminBlock,
  type AdminPage,
  type BlockWrite,
  type PageTemplate,
} from '../../services/adminService'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import BlockEditor from '../components/BlockEditor'
import BlockRenderer, {
  type BlockRenderData,
} from '../../components/blocks/BlockRenderer'
import type { Block } from '../../services/pagesService'

const TYPE_LABELS: Record<string, string> = {
  hero: 'Hero banner',
  richtext: 'Rich text',
  html: 'Custom HTML',
  image: 'Image',
  button: 'Button',
  divider: 'Divider',
  agenda: 'Agenda',
  speakers: 'Speakers',
  content: 'Content list',
}

function blockSummary(b: AdminBlock): string {
  const c = b.config || {}
  return (
    (c.title as string) ||
    (c.label as string) ||
    (c.alt as string) ||
    (b.source ? b.source.slice(0, 40) : '') ||
    TYPE_LABELS[b.blockType] ||
    b.blockType
  )
}

export default function PageBuilder({
  eventId,
  pageId,
  onBack,
}: {
  eventId: string
  pageId: string
  onBack?: () => void
}) {
  const { notify } = useToast()
  const [page, setPage] = useState<AdminPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<
    { block: AdminBlock | null; parentId?: string; column?: number } | null
  >(null)
  const [mode, setMode] = useState<'edit' | 'preview'>('edit')
  const [previewData, setPreviewData] = useState<BlockRenderData | null>(null)
  const [templateOpen, setTemplateOpen] = useState<null | 'save' | 'insert'>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const load = () =>
    adminService
      .getPage(eventId, pageId)
      .then(setPage)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, pageId])

  // Fetch this event's live data for the preview's dynamic blocks (once, lazily
  // on first switch to preview).
  useEffect(() => {
    if (mode !== 'preview' || previewData) return
    Promise.all([
      adminService.listSessions(eventId).catch(() => []),
      adminService.listSpeakers(eventId).catch(() => []),
      adminService.listContent(eventId).catch(() => []),
      adminService.getEvent(eventId).catch(() => null),
    ]).then(([sessions, speakers, content, event]) =>
      setPreviewData({ sessions, speakers, content, event }),
    )
  }, [mode, previewData, eventId])

  const onDragEnd = async (e: DragEndEvent) => {
    if (!page || !e.over || e.active.id === e.over.id) return
    const ids = page.blocks.map((b) => b.id)
    const from = ids.indexOf(String(e.active.id))
    const to = ids.indexOf(String(e.over.id))
    const next = arrayMove(page.blocks, from, to)
    setPage({ ...page, blocks: next })
    try {
      await adminService.reorderBlocks(eventId, pageId, next.map((b) => b.id))
    } catch (err) {
      notify(apiError(err), 'error')
      load()
    }
  }

  const saveBlock = async (payload: BlockWrite) => {
    try {
      if (editing?.block) {
        await adminService.updateBlock(eventId, editing.block.id, payload)
      } else {
        await adminService.createBlock(eventId, pageId, {
          ...payload,
          parentBlockId: editing?.parentId ?? null,
          config: {
            ...(payload.config ?? {}),
            ...(editing?.column != null ? { column: editing.column } : {}),
          },
        })
      }
      setEditing(null)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const duplicate = async (b: AdminBlock) => {
    try {
      await adminService.duplicateBlock(eventId, b.id)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const removeBlock = async (b: AdminBlock) => {
    if (!(await confirmDelete(`Delete this ${TYPE_LABELS[b.blockType] || 'block'}?`)))
      return
    try {
      await adminService.deleteBlock(eventId, b.id)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />
  if (!page) return null

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          {onBack && (
            <Button size="small" color="inherit" onClick={onBack} sx={{ mb: 0.5 }}>
              ← All pages
            </Button>
          )}
          <Typography variant="h6" fontWeight={700}>
            {page.title}
            {page.isHome && (
              <Chip label="Home" size="small" sx={{ ml: 1, height: 20, fontWeight: 700 }} />
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, next) => {
              if (next) setMode(next)
            }}
          >
            <ToggleButton value="edit" aria-label="Edit">
              <EditNoteRoundedIcon fontSize="small" sx={{ mr: 0.5 }} /> Edit
            </ToggleButton>
            <ToggleButton value="preview" aria-label="Preview">
              <VisibilityRoundedIcon fontSize="small" sx={{ mr: 0.5 }} /> Preview
            </ToggleButton>
          </ToggleButtonGroup>
          {mode === 'edit' && (
            <>
              <Button size="small" onClick={() => setTemplateOpen('insert')}>
                Insert template
              </Button>
              <Button size="small" onClick={() => setTemplateOpen('save')}>
                Save as template
              </Button>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => setEditing({ block: null })}
              >
                Add block
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {mode === 'preview' ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          {previewData ? (
            <Container maxWidth="md" sx={{ py: 4 }}>
              {!page.isHome && (
                <Typography variant="h4" fontWeight={800} sx={{ mb: 3 }}>
                  {page.title}
                </Typography>
              )}
              {page.blocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  This page has no blocks yet.
                </Typography>
              ) : (
                <BlockRenderer
                  blocks={page.blocks as unknown as Block[]}
                  data={previewData}
                />
              )}
            </Container>
          ) : (
            <LoadingState />
          )}
        </Box>
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={page.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <Stack spacing={1.5}>
                {page.blocks.map((b) => (
                  <SortableBlock
                    key={b.id}
                    block={b}
                    onEdit={() => setEditing({ block: b })}
                    onDelete={() => removeBlock(b)}
                    onDuplicate={() => duplicate(b)}
                    onEditChild={(child) => setEditing({ block: child })}
                    onDeleteChild={(child) => removeBlock(child)}
                    onAddChild={(col) =>
                      setEditing({ block: null, parentId: b.id, column: col })
                    }
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>

          {page.blocks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No blocks yet — add your first block to start building this page.
            </Typography>
          )}
        </>
      )}

      {editing && (
        <BlockEditor
          eventId={eventId}
          block={editing.block}
          onClose={() => setEditing(null)}
          onSave={saveBlock}
        />
      )}

      {templateOpen === 'save' && (
        <SaveTemplateDialog
          eventId={eventId}
          pageId={pageId}
          onClose={() => setTemplateOpen(null)}
        />
      )}
      {templateOpen === 'insert' && (
        <InsertTemplateDialog
          eventId={eventId}
          pageId={pageId}
          onClose={() => setTemplateOpen(null)}
          onInserted={() => {
            setTemplateOpen(null)
            load()
          }}
        />
      )}
    </Box>
  )
}

function SaveTemplateDialog({
  eventId,
  pageId,
  onClose,
}: {
  eventId: string
  pageId: string
  onClose: () => void
}) {
  const { notify } = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const save = async () => {
    if (!name) {
      notify('Name is required', 'error')
      return
    }
    setSaving(true)
    try {
      await adminService.savePageTemplate(eventId, pageId, { name, description })
      notify('Saved to template library')
      onClose()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Save page as template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function InsertTemplateDialog({
  eventId,
  pageId,
  onClose,
  onInserted,
}: {
  eventId: string
  pageId: string
  onClose: () => void
  onInserted: () => void
}) {
  const { notify } = useToast()
  const [templates, setTemplates] = useState<PageTemplate[]>([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    adminService
      .listPageTemplates()
      .then(setTemplates)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const insert = async (t: PageTemplate) => {
    try {
      await adminService.insertPageTemplate(eventId, pageId, t.id)
      notify(`Inserted "${t.name}"`)
      onInserted()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Insert from template library</DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : templates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No templates saved yet. Use “Save as template” to add one.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {templates.map((t) => (
              <Card
                key={t.id}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.scope}
                    {t.description ? ` · ${t.description}` : ''}
                  </Typography>
                </Box>
                <Button size="small" variant="contained" onClick={() => insert(t)}>
                  Insert
                </Button>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function SortableBlock({
  block,
  onEdit,
  onDelete,
  onDuplicate,
  onEditChild,
  onDeleteChild,
  onAddChild,
}: {
  block: AdminBlock
  onEdit: () => void
  onDelete: () => void
  onDuplicate: () => void
  onEditChild: (child: AdminBlock) => void
  onDeleteChild: (child: AdminBlock) => void
  onAddChild: (column: number) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id })
  const isColumns = block.blockType === 'columns'
  const colCount = Number(block.config?.columnCount ?? 2)
  return (
    <Card
      ref={setNodeRef}
      variant="outlined"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        p: 1.5,
        borderRadius: 2,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton size="small" sx={{ cursor: 'grab' }} {...attributes} {...listeners}>
          <DragIndicatorRoundedIcon fontSize="small" />
        </IconButton>
        <Chip
          label={TYPE_LABELS[block.blockType] || block.blockType}
          size="small"
          sx={{ height: 22, fontWeight: 700 }}
        />
        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}>
          {blockSummary(block)}
        </Typography>
        <IconButton size="small" onClick={onEdit} title="Edit">
          <EditRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onDuplicate} title="Duplicate">
          <ContentCopyRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={onDelete} title="Delete">
          <DeleteRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>

      {isColumns && (
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          {Array.from({ length: colCount }).map((_, col) => {
            const kids = (block.children ?? []).filter(
              (c) => (c.config?.column as number ?? 0) === col,
            )
            return (
              <Grid item xs={12} md={12 / colCount} key={col}>
                <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Column {col + 1}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {kids.map((c) => (
                      <Stack key={c.id} direction="row" alignItems="center" gap={0.5}>
                        <Chip label={TYPE_LABELS[c.blockType] || c.blockType} size="small" sx={{ height: 20 }} />
                        <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                          {blockSummary(c)}
                        </Typography>
                        <IconButton size="small" onClick={() => onEditChild(c)}>
                          <EditRoundedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => onDeleteChild(c)}>
                          <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onAddChild(col)}>
                      Add
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            )
          })}
        </Grid>
      )}
    </Card>
  )
}
